import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/AdminUsers.css';

function AdminUsers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Redirect if not admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError('');
        
        const response = await fetch('https://n8n.iniai.cloud/webhook/get-all-users', {
          method: 'GET',
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Handle the response format - might be an array or have a users property
        let usersArray = [];
        if (Array.isArray(data)) {
          usersArray = data;
        } else if (data[0]?.users) {
          usersArray = data[0].users;
        } else if (data.users) {
          usersArray = data.users;
        } else if (data[0]?.success === 'true' && data[0]?.users) {
          usersArray = data[0].users;
        }
        
        setUsers(usersArray);
        
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to load users: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user]);

  const handleEditUser = (userId) => {
    navigate(`/admin/edit-user/${userId}`);
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user: ${userName}?`)) {
      return;
    }

    try {
      const response = await fetch('https://n8n.iniai.cloud/webhook/delete-user', {
        method: 'POST',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data[0]?.success === 'true' || data.success) {
        // Remove user from local state
        setUsers(users.filter(u => u.id !== userId));
      } else {
        setError('Failed to delete user: ' + (data[0]?.message || data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Error deleting user: ' + err.message);
    }
  };

  // Helper function to check if user can be deleted
  const canDeleteUser = (userItem) => {
    if (!user || !userItem) return false;
    
    // Can't delete yourself
    if (userItem.id === user.id) return false;
    if (userItem.email === user.email) return false;
    
    // Additional safety checks
    if (userItem.email === 'matthew.birkby@gmail.com') return false; // Your admin email
    
    return true;
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (user?.role !== 'admin') {
    return <div className="admin-users-container">Access denied. Admin role required.</div>;
  }

  if (loading) {
    return (
      <div className="admin-users-container">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6c757d', margin: 0 }}>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users-container">
      <div className="admin-users-header">
        <h2>User Management</h2>
        <button 
          className="create-user-btn"
          onClick={() => navigate('/admin/create-user')}
        >
          Create New User
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search users by name, email, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(userItem => (
              <tr key={userItem.id}>
                <td>{userItem.name || 'N/A'}</td>
                <td>{userItem.email || 'N/A'}</td>
                <td>
                  <span className={`role-badge role-${(userItem.role || 'user').toLowerCase().replace('-', '-')}`}>
                    {userItem.role || 'user'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge status-${(userItem.status || 'active').toLowerCase()}`}>
                    {userItem.status || 'active'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="edit-btn"
                      onClick={() => handleEditUser(userItem.id)}
                      title="Edit User"
                    >
                      Edit
                    </button>
                    {canDeleteUser(userItem) && (
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteUser(userItem.id, userItem.name)}
                        title="Delete User"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="no-users">
            {searchTerm ? 'No users found matching your search.' : 'No users found.'}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUsers;